import { useEffect, useRef, useState } from 'react';
import type { GeocodeResult } from '../lib/geocode';
import { searchPlaces } from '../lib/geocode';
import { parseAnyInput } from '../lib/gmaps';
import { applyParsedInput } from '../state/applyInput';
import { useAppStore, type Which } from '../state/store';
import { IconClear, IconCrosshair, IconLocate, IconSearch } from './Icons';

const NAMES: Record<Which, string> = { a: 'Partenza', b: 'Arrivo' };
const PLACEHOLDER: Record<Which, string> = { a: 'Da dove parti?', b: 'Dove vai?' };

type Parsed = NonNullable<ReturnType<typeof parseAnyInput>>;

/**
 * Campo di un punto (partenza o arrivo) nello stile dei campi di Google Maps:
 * ricerca luoghi (Nominatim), link di Google Maps incollati, coordinate,
 * «la mia posizione» e scelta sulla mappa dal menu dei suggerimenti.
 */
export function PlaceInput({ which }: { which: Which }) {
  const point = useAppStore((s) => s[which]);
  const other = useAppStore((s) => (which === 'a' ? s.b : s.a));
  const pickTarget = useAppStore((s) => s.pickTarget);
  const setPickTarget = useAppStore((s) => s.setPickTarget);
  const clearPoint = useAppStore((s) => s.clearPoint);
  const setPoint = useAppStore((s) => s.setPoint);
  const setHint = useAppStore((s) => s.setHint);

  const [text, setText] = useState('');
  const [focused, setFocused] = useState(false);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState<GeocodeResult[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const searchSeq = useRef(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const picking = pickTarget === which;
  const showSuggestions = focused && text.trim().length === 0;
  const listOpen = focused && (results.length > 0 || showSuggestions);

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  const closeList = () => setResults([]);
  const blur = () => inputRef.current?.blur();

  const runSearch = (q: string, immediate = false) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const mySeq = ++searchSeq.current;
    const exec = async () => {
      try {
        const found = await searchPlaces(q, { limit: 5, near: other?.ll });
        if (searchSeq.current === mySeq) setResults(found);
      } catch {
        if (searchSeq.current === mySeq) setResults([]);
      }
    };
    if (immediate) void exec();
    else debounceRef.current = setTimeout(() => void exec(), 400);
  };

  const applyNonQuery = (parsed: Parsed) => {
    setBusy(true);
    applyParsedInput(which, parsed)
      .then((ok) => {
        if (ok) {
          setText('');
          closeList();
          blur();
        }
      })
      .catch((err: unknown) => {
        const msg =
          err && typeof err === 'object' && 'userMessage' in err
            ? String((err as { userMessage: string }).userMessage)
            : 'Non sono riuscito a interpretare il link.';
        setHint(msg);
      })
      .finally(() => setBusy(false));
  };

  const handleTextChange = (v: string) => {
    setText(v);
    const q = v.trim();
    if (q.length < 3 || /^https?:\/\//i.test(q) || /^geo:/i.test(q)) {
      closeList();
      return;
    }
    const parsed = parseAnyInput(q);
    if (parsed?.kind === 'query') runSearch(q);
    else closeList();
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData('text').trim();
    const parsed = parseAnyInput(pasted);
    if (parsed && parsed.kind !== 'query') {
      e.preventDefault();
      applyNonQuery(parsed);
    }
  };

  const selectResult = (r: GeocodeResult) => {
    setPoint(which, r.ll, r.label);
    setText('');
    closeList();
    blur();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      closeList();
      blur();
      return;
    }
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (results.length > 0) {
      selectResult(results[0]);
      return;
    }
    const parsed = parseAnyInput(text);
    if (!parsed) return;
    if (parsed.kind === 'query') runSearch(text.trim(), true);
    else applyNonQuery(parsed);
  };

  const locate = () => {
    blur();
    if (!('geolocation' in navigator)) {
      setHint('Il browser non espone la geolocalizzazione.');
      return;
    }
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setBusy(false);
        setPoint(
          which,
          { lat: pos.coords.latitude, lng: pos.coords.longitude },
          'La mia posizione',
        );
      },
      () => {
        setBusy(false);
        setHint('Non riesco a leggere la posizione: controlla i permessi del browser.');
      },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 60_000 },
    );
  };

  const pickOnMap = () => {
    blur();
    setPickTarget(which);
  };

  const prevent = (e: React.MouseEvent) => e.preventDefault();

  return (
    <div className={`place${picking ? ' place--picking' : ''}`}>
      <label className="sr-only" htmlFor={`place-${which}`}>
        {NAMES[which]}
      </label>
      <input
        id={`place-${which}`}
        ref={inputRef}
        className="field place__input"
        type="text"
        autoComplete="off"
        spellCheck={false}
        placeholder={picking ? 'Tocca un punto sulla mappa…' : PLACEHOLDER[which]}
        value={focused ? text : (point?.label ?? '')}
        onFocus={() => {
          setFocused(true);
          setText('');
        }}
        onBlur={() => {
          // ritarda la chiusura per permettere il click sui risultati
          setTimeout(() => {
            setFocused(false);
            closeList();
          }, 180);
        }}
        onChange={(e) => handleTextChange(e.target.value)}
        onPaste={handlePaste}
        onKeyDown={handleKeyDown}
        aria-expanded={listOpen}
        aria-controls={`place-list-${which}`}
      />
      <div className="place__actions">
        {busy ? (
          <span className="mini-spinner" aria-label="Attendi…" />
        ) : point && !focused ? (
          <button
            type="button"
            className="icon-btn place__clear"
            title={`Rimuovi ${NAMES[which].toLowerCase()}`}
            aria-label={`Rimuovi ${NAMES[which].toLowerCase()}`}
            onClick={() => {
              clearPoint(which);
              setText('');
            }}
          >
            <IconClear size={16} />
          </button>
        ) : null}
      </div>

      {listOpen && (
        <ul className="place__results" role="listbox" id={`place-list-${which}`}>
          {showSuggestions ? (
            <>
              <li>
                <button
                  type="button"
                  role="option"
                  aria-selected={false}
                  onMouseDown={prevent}
                  onClick={locate}
                >
                  <span className="place__ico place__ico--blue">
                    <IconLocate size={16} />
                  </span>
                  <span>La mia posizione</span>
                </button>
              </li>
              <li>
                <button
                  type="button"
                  role="option"
                  aria-selected={false}
                  onMouseDown={prevent}
                  onClick={pickOnMap}
                >
                  <span className="place__ico">
                    <IconCrosshair size={16} />
                  </span>
                  <span>Scegli sulla mappa</span>
                </button>
              </li>
              <li className="place__hint">
                Oppure scrivi un indirizzo, incolla un link di Google Maps o delle coordinate.
              </li>
            </>
          ) : (
            results.map((r, i) => (
              <li key={`${r.ll.lat}-${r.ll.lng}-${i}`}>
                <button
                  type="button"
                  role="option"
                  aria-selected={i === 0}
                  onMouseDown={prevent}
                  onClick={() => selectResult(r)}
                >
                  <span className="place__ico">
                    <IconSearch size={15} />
                  </span>
                  <span className="grow place__text">{r.label}</span>
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
